import { AppTextField, AppTable } from '@/components/atoms';
import type { CreateMassagerFormData } from '@/schemas/createMassagerSchema';
import { Col, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

type SortDir = 'ascend' | 'descend';

export type InsurerSortField =
  | 'wide_area_union_code'
  | 'wide_area_union_number'
  | 'prefecture_number';

export type WideAreaRowsName = 'wide_area_union_rows' | 'massage_wide_area_union_rows';

interface MassagerInsurerTableProps {
  control: Control<CreateMassagerFormData>;
  errors: FieldErrors<CreateMassagerFormData>;
  /** A2用 = wide_area_union_rows, R2用 = massage_wide_area_union_rows（BOTH 時に2つ表示） */
  rowsName?: WideAreaRowsName;
  /** 例: "はり・きゅう" / "マッサージ"（BOTH 時） */
  blockTitle?: string;
  prefectureFilter?: string;
  setPrefectureFilter?: (val: string) => void;
  rowMatchesFilter?: boolean;
  insurerSortField?: InsurerSortField | null;
  setInsurerSortField?: (field: InsurerSortField) => void;
  insurerSortDir?: SortDir;
  setInsurerSortDir?: (dir: SortDir) => void;
  /** 編集時: 表示順（ソート列クリック時のみ更新、入力中は変更しない） */
  displayOrder?: number[];
  /** false = 新規作成（単一行）, true = 編集（配列） */
  isEdit?: boolean;
  disabled?: boolean;
}

export const MassagerInsurerTable: React.FC<MassagerInsurerTableProps> = ({
  control,
  errors,
  rowsName = 'wide_area_union_rows',
  blockTitle: _blockTitle,
  prefectureFilter: prefectureFilterProp,
  setPrefectureFilter: setPrefectureFilterProp,
  rowMatchesFilter: _rowMatchesFilter,
  insurerSortField: insurerSortFieldProp,
  setInsurerSortField: setInsurerSortFieldProp,
  insurerSortDir: insurerSortDirProp,
  setInsurerSortDir: setInsurerSortDirProp,
  displayOrder: displayOrderProp = [],
  isEdit = false,
  disabled = false,
}) => {
  const [internalFilter, setInternalFilter] = useState('');
  const [internalSortField, setInternalSortField] = useState<InsurerSortField | null>(
    'wide_area_union_code'
  );
  const [internalSortDir, setInternalSortDir] = useState<SortDir>('ascend');
  const [internalDisplayOrder, setInternalDisplayOrder] = useState<number[]>([]);

  const prefectureFilter = prefectureFilterProp ?? internalFilter;
  const setPrefectureFilter = setPrefectureFilterProp ?? setInternalFilter;
  const insurerSortField = insurerSortFieldProp ?? internalSortField;
  const setInsurerSortField = setInsurerSortFieldProp ?? setInternalSortField;
  const insurerSortDir = insurerSortDirProp ?? internalSortDir;
  const setInsurerSortDir = setInsurerSortDirProp ?? setInternalSortDir;
  const displayOrder = displayOrderProp.length > 0 ? displayOrderProp : internalDisplayOrder;

  const { fields } = useFieldArray({
    control,
    name: rowsName,
  });

  const watchedRows = useWatch({ control, name: rowsName, defaultValue: [] });
  const rows = Array.isArray(watchedRows) ? watchedRows : [];
  const filterTrim = prefectureFilter.trim();
  const useInternalDisplayOrder = displayOrderProp.length === 0;

  useEffect(() => {
    if (useInternalDisplayOrder && isEdit && rows.length > 0) {
      setInternalDisplayOrder(prev => (prev.length !== rows.length ? rows.map((_, i) => i) : prev));
    } else if (useInternalDisplayOrder && !isEdit) {
      setInternalDisplayOrder([]);
    }
  }, [useInternalDisplayOrder, isEdit, rows.length]);

  useEffect(() => {
    if (!useInternalDisplayOrder || !isEdit || rows.length === 0) return;
    const sortField = insurerSortField ?? 'wide_area_union_code';
    const indices = Array.from({ length: rows.length }, (_, i) => i);
    const sorted = [...indices].sort((a, b) => {
      const va = (rows[a]?.[sortField] ?? '').toString().trim();
      const vb = (rows[b]?.[sortField] ?? '').toString().trim();
      const cmp = va.localeCompare(vb, 'ja');
      return insurerSortDir === 'ascend' ? cmp : -cmp;
    });
    setInternalDisplayOrder(sorted);
  }, [useInternalDisplayOrder, insurerSortField, insurerSortDir, isEdit, rows.length]);

  // RA2: 表示順は displayOrder（ソートクリック時のみ更新）。県番号で絞り込み（A2/R2 とも create/edit で適用）
  const indicesToShow = isEdit
    ? (displayOrder.length > 0 ? displayOrder : fields.map((_, i) => i)).filter(
        i =>
          !filterTrim || (rows[i]?.prefecture_number ?? '').toString().trim().includes(filterTrim)
      )
    : filterTrim
      ? (() => {
          const match = (rows[0]?.prefecture_number ?? '').toString().trim().includes(filterTrim);
          return match ? [0] : [];
        })()
      : [0];

  const visibleCount = indicesToShow.length;
  const totalCount = isEdit ? fields.length : 1;

  // build dataSource for AppTable
  const dataSource = React.useMemo(() => {
    if (isEdit) {
      return indicesToShow.map(i => ({
        ...rows[i],
        _idx: i,
        key: fields[i]?.id ?? i,
      }));
    }
    // single row new: 県番号で絞り込みに一致するときだけ表示、一致しなければ空で「表示するデータがありません」を表示
    if (indicesToShow.length === 0) {
      return [];
    }
    return [
      {
        ...rows[0],
        _idx: 0,
        key: 'new',
      },
    ];
  }, [isEdit, indicesToShow, rows, fields]);

  const columns = React.useMemo(
    () => [
      {
        title: '保険者番号',
        dataIndex: 'wide_area_union_code',
        key: 'wide_area_union_code',
        sorter: true,
        width: '33%',
        render: (_val: any, record: any) => {
          const idx = record._idx as number;
          const fieldName = isEdit
            ? `${rowsName}.${idx}.wide_area_union_code`
            : `${rowsName}.0.wide_area_union_code`;
          const errBag = (errors as any)[rowsName]?.[idx];
          const errMsg = errBag?.wide_area_union_code?.message;
          const rowHasError =
            Boolean(errBag?.wide_area_union_code?.message) ||
            Boolean(errBag?.wide_area_union_number?.message) ||
            Boolean(errBag?.prefecture_number?.message);
          return (
            <Controller
              name={fieldName as any}
              control={control}
              render={({ field: f }) => (
                <>
                  <div className="flex h-[39px] items-center">
                    <AppTextField
                      {...f}
                      value={f.value ?? ''}
                      status={errMsg ? 'error' : undefined}
                      className="w-full"
                      disabled={disabled}
                    />
                  </div>
                  <p
                    className={`text-sm leading-tight line-clamp-1 ${
                      rowHasError ? 'mt-1 h-4' : 'h-0 overflow-hidden'
                    }`}
                  >
                    <span className={errMsg ? 'text-red-500' : ''}>{errMsg ?? ''}</span>
                  </p>
                </>
              )}
            />
          );
        },
      },
      {
        title: '施術機関コード',
        dataIndex: 'wide_area_union_number',
        key: 'wide_area_union_number',
        sorter: true,
        width: '33%',
        render: (_val: any, record: any) => {
          const idx = record._idx as number;
          const fieldName = isEdit
            ? `${rowsName}.${idx}.wide_area_union_number`
            : `${rowsName}.0.wide_area_union_number`;
          const errBag = (errors as any)[rowsName]?.[idx];
          const errMsg = errBag?.wide_area_union_number?.message;
          const rowHasError =
            Boolean(errBag?.wide_area_union_code?.message) ||
            Boolean(errBag?.wide_area_union_number?.message) ||
            Boolean(errBag?.prefecture_number?.message);
          return (
            <Controller
              name={fieldName as any}
              control={control}
              render={({ field: f }) => (
                <>
                  <div className="flex h-[39px] items-center">
                    <AppTextField
                      {...f}
                      value={f.value ?? ''}
                      status={errMsg ? 'error' : undefined}
                      className="w-full"
                      disabled={disabled}
                    />
                  </div>
                  <p
                    className={`text-sm leading-tight line-clamp-1 ${
                      rowHasError ? 'mt-1 h-4' : 'h-0 overflow-hidden'
                    }`}
                  >
                    <span className={errMsg ? 'text-red-500' : ''}>{errMsg ?? ''}</span>
                  </p>
                </>
              )}
            />
          );
        },
      },
      {
        title: '県番号',
        dataIndex: 'prefecture_number',
        key: 'prefecture_number',
        sorter: true,
        width: '34%',
        render: (_val: any, record: any) => {
          const idx = record._idx as number;
          const fieldName = isEdit
            ? `${rowsName}.${idx}.prefecture_number`
            : `${rowsName}.0.prefecture_number`;
          const errBag = (errors as any)[rowsName]?.[idx];
          const errMsg = errBag?.prefecture_number?.message;
          const rowHasError =
            Boolean(errBag?.wide_area_union_code?.message) ||
            Boolean(errBag?.wide_area_union_number?.message) ||
            Boolean(errBag?.prefecture_number?.message);
          return (
            <Controller
              name={fieldName as any}
              control={control}
              render={({ field: f }) => (
                <>
                  <div className="flex h-[39px] items-center">
                    <AppTextField
                      {...f}
                      value={f.value ?? ''}
                      status={errMsg ? 'error' : undefined}
                      className="w-full"
                      disabled={disabled}
                    />
                  </div>
                  <p
                    className={`text-sm leading-tight line-clamp-1 ${
                      rowHasError ? 'mt-1 h-4' : 'h-0 overflow-hidden'
                    }`}
                  >
                    <span className={errMsg ? 'text-red-500' : ''}>{errMsg ?? ''}</span>
                  </p>
                </>
              )}
            />
          );
        },
      },
    ],
    [control, errors, insurerSortDir, insurerSortField, isEdit, rows, fields, rowsName, disabled]
  );

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <div className="mb-1">
          <label className="font-medium h-[39px] flex items-center">保険者別番号</label>
        </div>
        <div className="mb-2 flex items-center justify-end gap-2">
          <label
            htmlFor="prefecture-filter"
            className="text-sm font-medium text-gray-700 whitespace-nowrap"
          >
            県番号で絞り込み
          </label>
          <AppTextField
            id="prefecture-filter"
            placeholder=""
            value={prefectureFilter}
            onChange={e => setPrefectureFilter(e.target.value)}
            allowClear
            className="w-32 !h-[39px]"
            disabled={disabled}
          />
        </div>
        {/* Let Ant Table handle its own borders to avoid double rounded corners inside fieldset */}
        <div className="overflow-hidden">
          <AppTable
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            rowKey="key"
            className="w-full min-w-0"
            tableLayout="fixed"
            scroll={{ x: '100%' }}
            onChange={(_pagination, _filters, sorter) => {
              const sort = Array.isArray(sorter) ? sorter[0] : sorter;
              if (sort && sort.order) {
                setInsurerSortField(sort.field as InsurerSortField);
                setInsurerSortDir(sort.order as SortDir);
              }
            }}
          />
        </div>
        <div className="mt-2 px-1 text-sm text-gray-600">
          {totalCount} 件中 1件から{visibleCount}件 を表示しています
        </div>
      </Col>
    </Row>
  );
};
